import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Tabs,
    Tab,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    BarChart as BarChartIcon,
    Groups as TeamIcon,
    Person as PersonIcon,
    SupervisorAccount as AdminIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import { useQuery } from '@tanstack/react-query';

import StatistikyFilterBar, { FilterValues } from '../components/reports/StatistikyFilterBar';
import ExportMenu from '../components/reports/ExportMenu';
import TeamTrendChart from '../components/TeamTrendChart';
import OsobniStatistikaCard, { StatistikaCardContent } from '../components/OsobniStatistikaCard';

import {
    useReportDetailQuery,
    useOsobniStatistikaQuery,
    useStatistikaUzivateleQuery,
} from '../api/JsdhNotifikaceService/service/StatistikaMutations';
import { useAllUsersQuery } from '../api/JsdhNotifikaceService/service/AdminMutations';
import { useAuth } from '../context/AuthContext';
import { formatujDobuReakce } from '../utils/statistikyUtils';
import type { TymoveGrafyResponse } from '../api/JsdhNotifikaceService/generated/models/TymoveGrafy';
import type { ReportFilterParams } from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import JsdhNotifikaceService from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import type { PohotovostReportRow } from '../api/JsdhNotifikaceService/generated/models/ReportDetail';

type PageTab = 'prehled' | 'tym' | 'clen';

// ─── KPI card ────────────────────────────────────────────────────────────────

interface KpiCardProps {
    label: string;
    value: string | number;
    subtitle?: string;
    delta?: number | null;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, subtitle, delta }) => {
    const DeltaIcon =
        delta == null ? null :
        delta > 0 ? TrendingUpIcon :
        delta < 0 ? TrendingDownIcon :
        TrendingFlatIcon;

    const deltaColor =
        delta == null ? 'text.secondary' :
        delta > 0 ? 'success.main' :
        delta < 0 ? 'error.main' :
        'text.secondary';

    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {label}
                </Typography>
                <Typography variant="h4" component="div" fontWeight={600}>
                    {value}
                </Typography>
                {delta != null && DeltaIcon && (
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                        <DeltaIcon fontSize="small" sx={{ color: deltaColor }} />
                        <Typography variant="caption" sx={{ color: deltaColor }}>
                            {delta > 0 ? '+' : ''}{Math.round(delta)} % vs. předchozí období
                        </Typography>
                    </Box>
                )}
                {subtitle && delta == null && (
                    <Typography variant="caption" color="text.secondary">
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

// ─── Rozpad row (proportional bar) ───────────────────────────────────────────

interface RozpadRowProps {
    label: string;
    value: number;
    total: number;
    color: string;
}

const RozpadRow: React.FC<RozpadRowProps> = ({ label, value, total, color }) => {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <Box sx={{ mb: 1 }}>
            <Box display="flex" justifyContent="space-between" mb={0.25}>
                <Typography variant="body2">{label}</Typography>
                <Typography variant="body2" fontWeight={600}>
                    {value} ({pct} %)
                </Typography>
            </Box>
            <Box
                sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'action.hover',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        width: `${pct}%`,
                        bgcolor: color,
                        borderRadius: 3,
                        transition: 'width 0.4s ease',
                    }}
                />
            </Box>
        </Box>
    );
};

// ─── Section heading helper ───────────────────────────────────────────────────

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {children}
        </Typography>
        <Divider sx={{ mb: 2 }} />
    </>
);

// ─── Pohotovost sub-section ───────────────────────────────────────────────────

const PohotovostSection: React.FC<{ pohotovost: PohotovostReportRow }> = ({ pohotovost }) => {
    const hasNotes = pohotovost.odpovedi.some((r) => r.poznamka);
    const heading = pohotovost.kategorieLabel
        ? `${pohotovost.nazev} — ${pohotovost.kategorieLabel}`
        : pohotovost.nazev;
    const od = dayjs(pohotovost.platnostOd).format('DD.MM.YYYY HH:mm');
    const do_ = pohotovost.platnostDo ? dayjs(pohotovost.platnostDo).format('DD.MM.YYYY HH:mm') : '…';

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight={700} gutterBottom>
                {heading}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Období: {od} – {do_} &nbsp;&middot;&nbsp; Vyhlásil: {pohotovost.vyhlasil} &nbsp;&middot;&nbsp;
                Dostupných: {pohotovost.pocetDostupnych} &nbsp;&middot;&nbsp; Nedostupných: {pohotovost.pocetNedostupnych}
            </Typography>
            {pohotovost.odpovedi.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Bez odpovědí.
                </Typography>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Jméno</TableCell>
                                <TableCell>Funkce</TableCell>
                                <TableCell>Odpověď</TableCell>
                                {hasNotes && <TableCell>Poznámka</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pohotovost.odpovedi.map((r) => (
                                <TableRow key={r.uzivatelId} hover>
                                    <TableCell>{r.jmeno}</TableCell>
                                    <TableCell>{r.funkce}</TableCell>
                                    <TableCell
                                        sx={{
                                            color: r.odpoved === 'DOSTUPNY' ? 'success.main' : 'error.main',
                                            fontWeight: 500,
                                        }}
                                    >
                                        {r.odpoved === 'DOSTUPNY' ? 'Dostupný' : 'Nedostupný'}
                                    </TableCell>
                                    {hasNotes && (
                                        <TableCell sx={{ color: 'text.secondary' }}>
                                            {r.poznamka ?? ''}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const StatistikyReportyPage: React.FC = () => {
    const { isAdmin } = useAuth();

    const [activeTab, setActiveTab] = useState<PageTab>('prehled');
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

    const [filter, setFilter] = useState<FilterValues>({
        dateFrom: dayjs().subtract(30, 'day'),
        dateTo: dayjs(),
        typ: null,
        podtyp: null,
        preset: '30d',
    });

    // Build params for API calls
    const params: ReportFilterParams = useMemo(() => ({
        dateFrom: filter.dateFrom?.format('YYYY-MM-DD') ?? undefined,
        dateTo: filter.dateTo?.format('YYYY-MM-DD') ?? undefined,
        typ: filter.typ ?? undefined,
        podtyp: filter.podtyp ?? undefined,
    }), [filter]);

    // Queries
    const { data: detail, isLoading: detailLoading, error: detailError } = useReportDetailQuery(params);

    const { data: tymoveGrafy, isLoading: tymLoading, error: tymError } = useQuery<TymoveGrafyResponse>({
        queryKey: ['tymoveGrafy', params],
        queryFn: () => JsdhNotifikaceService.getTymoveGrafy(params),
        enabled: activeTab === 'tym',
    });

    const { data: osobniStats, isLoading: osobniLoading, error: osobniError } = useOsobniStatistikaQuery(
        params,
        activeTab === 'clen' && !isAdmin,
    );

    const { data: users, isLoading: usersLoading } = useAllUsersQuery();
    const activeUsers = useMemo(() => users?.filter((u) => u.aktivni) ?? [], [users]);

    const { data: userStats, isLoading: userStatsLoading, error: userStatsError } = useStatistikaUzivateleQuery(
        activeTab === 'clen' && isAdmin ? selectedUserId : null,
        params
    );

    // Derived state — sourced from detail.souhrn
    const noVyjezdy = !detailLoading && (detail?.souhrn?.pocetVyjezdu ?? 0) === 0;

    const souhrn = detail?.souhrn ?? null;

    const rozpadJedu = souhrn?.rozpadDostupnosti.jedu ?? 0;
    const rozpadNejeduManual = souhrn?.rozpadDostupnosti.nejeduManual ?? 0;
    const rozpadAuto = souhrn
        ? (souhrn.rozpadDostupnosti.hzsSmena +
           souhrn.rozpadDostupnosti.pohotovost +
           souhrn.rozpadDostupnosti.nedostupny)
        : 0;
    const rozpadTotal = rozpadJedu + rozpadNejeduManual + rozpadAuto;

    const filterDateLabel = useMemo(() => {
        if (!filter.dateFrom && !filter.dateTo) return 'celé období';
        const od = filter.dateFrom?.format('D.M.YYYY') ?? '—';
        const do_ = filter.dateTo?.format('D.M.YYYY') ?? '—';
        return `${od} – ${do_}`;
    }, [filter.dateFrom, filter.dateTo]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
            <Box sx={{ p: 3 }}>
                {/* Page header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={1}>
                    <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                        Statistiky &amp; Reporty
                    </Typography>
                    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                        <ExportMenu params={params} disabled={detailLoading || noVyjezdy} />
                        <Typography variant="caption" color="text.secondary">
                            Export reportu jednotky za zvolené období
                        </Typography>
                    </Box>
                </Box>

                {/* Filter bar */}
                <StatistikyFilterBar values={filter} onChange={setFilter} />

                {/* Active filter summary */}
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                    <Chip label={filterDateLabel} size="small" variant="outlined" />
                    {filter.typ && <Chip label={filter.typ} size="small" variant="outlined" />}
                    {filter.podtyp && <Chip label={filter.podtyp} size="small" variant="outlined" />}
                </Box>

                {/* Tabs */}
                <Paper sx={{ mb: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v as PageTab)}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab value="prehled" label="Přehled" icon={<BarChartIcon />} iconPosition="start" />
                        <Tab value="tym" label="Tým" icon={<TeamIcon />} iconPosition="start" />
                        <Tab
                            value="clen"
                            label={isAdmin ? 'Člen' : 'Moje statistiky'}
                            icon={isAdmin ? <AdminIcon /> : <PersonIcon />}
                            iconPosition="start"
                        />
                    </Tabs>
                </Paper>

                {/* ── PŘEHLED tab ────────────────────────────────────────── */}
                {activeTab === 'prehled' && (
                    <>
                        {detailLoading ? (
                            <Box display="flex" justifyContent="center" p={4}>
                                <CircularProgress />
                            </Box>
                        ) : detailError ? (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                Chyba při načítání dat: {(detailError as Error).message}
                            </Alert>
                        ) : (
                            <>
                                {noVyjezdy && (
                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        Žádné výjezdy v tomto období — upravte filtr pro zobrazení dat.
                                    </Alert>
                                )}

                                {/* a) KPI cards */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <KpiCard
                                            label="Výjezdů celkem"
                                            value={souhrn?.pocetVyjezdu ?? 0}
                                            delta={souhrn?.deltaVyjezduProcento ?? null}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <KpiCard
                                            label="Prům. doba reakce"
                                            value={formatujDobuReakce(souhrn?.prumernaDobaReakceSec ?? null)}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <KpiCard
                                            label="Prům. účast"
                                            value={souhrn ? `${Math.round(souhrn.prumernaUcastProcento)} %` : '—'}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <KpiCard
                                            label="Aktivních členů"
                                            value={souhrn?.pocetAktivnichClenu ?? '—'}
                                            subtitle="ve sledovaném období"
                                        />
                                    </Grid>
                                </Grid>

                                {/* b) Rozpad reakcí — 3 kategorie */}
                                {souhrn && souhrn.pocetVyjezdu > 0 && (
                                    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                                        <SectionHeading>Rozpad reakcí členů</SectionHeading>
                                        <Grid container spacing={3}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <RozpadRow
                                                    label="JEDU"
                                                    value={rozpadJedu}
                                                    total={rozpadTotal}
                                                    color="success.main"
                                                />
                                                <RozpadRow
                                                    label="NEJEDU (ručně)"
                                                    value={rozpadNejeduManual}
                                                    total={rozpadTotal}
                                                    color="error.main"
                                                />
                                                <RozpadRow
                                                    label="Automaticky omluveni"
                                                    value={rozpadAuto}
                                                    total={rozpadTotal}
                                                    color="info.main"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                )}

                                {/* c) Přehled výjezdů */}
                                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                                    <SectionHeading>Přehled výjezdů</SectionHeading>
                                    {!detail?.vyjezdy || detail.vyjezdy.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            V daném období jednotka neevidovala žádné výjezdy.
                                        </Typography>
                                    ) : (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Poř.</TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Datum a čas</TableCell>
                                                        <TableCell>Typ</TableCell>
                                                        <TableCell>Podtyp</TableCell>
                                                        <TableCell>Místo</TableCell>
                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Odpovědí</TableCell>
                                                        <TableCell align="right">JEDU</TableCell>
                                                        <TableCell align="right">NEJEDU</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {detail.vyjezdy.map((v, idx) => (
                                                        <TableRow key={v.id} hover>
                                                            <TableCell>{idx + 1}</TableCell>
                                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                                {dayjs(v.casVyhlaseni).format('DD.MM.YYYY HH:mm')}
                                                            </TableCell>
                                                            <TableCell>{v.typ}</TableCell>
                                                            <TableCell>{v.podtyp}</TableCell>
                                                            <TableCell>{v.misto}</TableCell>
                                                            <TableCell align="right">{v.pocetOdpovedi}</TableCell>
                                                            <TableCell align="right">{v.jedu}</TableCell>
                                                            <TableCell align="right">{v.nejedu}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {/* Totals row */}
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={5}
                                                            sx={{ fontWeight: 600, borderTop: '2px solid', borderColor: 'divider' }}
                                                        >
                                                            Celkem výjezdů: {detail.vyjezdy.length}
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            sx={{ fontWeight: 600, borderTop: '2px solid', borderColor: 'divider' }}
                                                        />
                                                        <TableCell
                                                            align="right"
                                                            sx={{ fontWeight: 600, borderTop: '2px solid', borderColor: 'divider' }}
                                                        >
                                                            {detail.vyjezdy.reduce((s, v) => s + v.jedu, 0)}
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            sx={{ fontWeight: 600, borderTop: '2px solid', borderColor: 'divider' }}
                                                        >
                                                            {detail.vyjezdy.reduce((s, v) => s + v.nejedu, 0)}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Paper>

                                {/* d) Výjezdy podle typu a podtypu */}
                                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                                    <SectionHeading>Výjezdy podle typu a podtypu</SectionHeading>
                                    {!detail?.vyjezdyPodleTypu || detail.vyjezdyPodleTypu.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            V daném období jednotka neevidovala žádné výjezdy.
                                        </Typography>
                                    ) : (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Typ</TableCell>
                                                        <TableCell>Podtyp</TableCell>
                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Počet výjezdů</TableCell>
                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Podíl %</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {detail.vyjezdyPodleTypu.map((r, idx) => (
                                                        <TableRow key={idx} hover>
                                                            <TableCell>{r.typ}</TableCell>
                                                            <TableCell>{r.podtyp}</TableCell>
                                                            <TableCell align="right">{r.pocet}</TableCell>
                                                            <TableCell align="right">
                                                                {r.podilProcento.toFixed(1)} %
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Paper>

                                {/* e) Přehled pohotovostí */}
                                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                                    <SectionHeading>Přehled pohotovostí</SectionHeading>
                                    {!detail?.pohotovosti || detail.pohotovosti.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            V daném období nebyla vyhlášena žádná pohotovost.
                                        </Typography>
                                    ) : (
                                        detail.pohotovosti.map((p) => (
                                            <PohotovostSection key={p.id} pohotovost={p} />
                                        ))
                                    )}
                                </Paper>

                                {/* f) Účast členů */}
                                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                                    <SectionHeading>Přehled účasti členů</SectionHeading>
                                    {!detail?.ucast || detail.ucast.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            V daném období nejsou k dispozici data o účasti členů.
                                        </Typography>
                                    ) : (
                                        <>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Jméno</TableCell>
                                                            <TableCell>Funkce</TableCell>
                                                            <TableCell>Stav</TableCell>
                                                            <TableCell align="right">Celkem</TableCell>
                                                            <TableCell align="right">JEDU</TableCell>
                                                            <TableCell align="right">NEJEDU</TableCell>
                                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Účast %</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {detail.ucast.map((u, idx) => (
                                                            <TableRow key={idx} hover>
                                                                <TableCell>{u.jmeno}</TableCell>
                                                                <TableCell>{u.funkce}</TableCell>
                                                                <TableCell>{u.stav}</TableCell>
                                                                <TableCell align="right">{u.celkemOdpovedi}</TableCell>
                                                                <TableCell align="right">{u.jedu}</TableCell>
                                                                <TableCell align="right">{u.nejedu}</TableCell>
                                                                <TableCell align="right">
                                                                    {u.pomerJedu.toFixed(1)} %
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                * Účast % = JEDU / (výjezdy × aktivní členové)
                                            </Typography>
                                        </>
                                    )}
                                </Paper>
                            </>
                        )}
                    </>
                )}

                {/* ── TÝM tab ────────────────────────────────────────────── */}
                {activeTab === 'tym' && (
                    <>
                        {tymLoading ? (
                            <Box display="flex" justifyContent="center" p={4}>
                                <CircularProgress />
                            </Box>
                        ) : tymError ? (
                            <Alert severity="error">
                                Chyba při načítání týmových grafů: {(tymError as Error).message}
                            </Alert>
                        ) : tymoveGrafy ? (
                            <>
                                <TeamTrendChart
                                    data={tymoveGrafy}
                                    allowedViews={['clenove', 'heatmapa', 'reakce']}
                                />
                            </>
                        ) : (
                            <Alert severity="info">
                                Data pro týmové grafy nejsou k dispozici
                            </Alert>
                        )}
                    </>
                )}

                {/* ── ČLEN tab ───────────────────────────────────────────── */}
                {activeTab === 'clen' && (
                    <>
                        {isAdmin ? (
                            <>
                                {/* Admin: member selector */}
                                <Paper sx={{ p: 2, mb: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Vyberte člena</InputLabel>
                                        <Select
                                            value={selectedUserId ?? ''}
                                            onChange={(e) => setSelectedUserId(e.target.value as number)}
                                            label="Vyberte člena"
                                        >
                                            {usersLoading ? (
                                                <MenuItem value="" disabled>Načítání…</MenuItem>
                                            ) : activeUsers.length === 0 ? (
                                                <MenuItem value="" disabled>Žádní aktivní členové</MenuItem>
                                            ) : (
                                                activeUsers.map((u) => (
                                                    <MenuItem key={u.id} value={u.id}>
                                                        {u.jmeno}
                                                        {u.funkce ? ` (${u.funkce})` : ''}
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                </Paper>

                                {!selectedUserId ? (
                                    <Alert severity="info">
                                        Vyberte člena pro zobrazení jeho statistik
                                    </Alert>
                                ) : userStatsLoading ? (
                                    <Box display="flex" justifyContent="center" p={4}>
                                        <CircularProgress />
                                    </Box>
                                ) : userStatsError ? (
                                    <Alert severity="error">
                                        Chyba při načítání statistik: {(userStatsError as Error).message}
                                    </Alert>
                                ) : userStats ? (
                                    <StatistikaCardContent
                                        stats={userStats}
                                        title={`Statistiky: ${userStats.jmeno}`}
                                        allowedViews={['trend', 'histogram', 'ucast', 'odpovedi']}
                                    />
                                ) : (
                                    <Alert severity="warning">
                                        Statistiky pro vybraného člena nebyly nalezeny
                                    </Alert>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Non-admin: own stats */}
                                {osobniLoading ? (
                                    <Box display="flex" justifyContent="center" p={4}>
                                        <CircularProgress />
                                    </Box>
                                ) : osobniError ? (
                                    <Alert severity="error">
                                        Chyba při načítání statistik: {(osobniError as Error).message}
                                    </Alert>
                                ) : osobniStats ? (
                                    <StatistikaCardContent
                                        stats={osobniStats}
                                        allowedViews={['trend', 'histogram', 'ucast', 'odpovedi']}
                                    />
                                ) : (
                                    <OsobniStatistikaCard />
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default StatistikyReportyPage;
